import {useCallback, useEffect, useRef} from 'react'
import {useSearchParams} from 'react-router-dom'
import {safeLocalStorage} from '@/lib/storage'
import {AppInstant, isValidInstantIso, localDateTimeForInput, localDateTimeToIso} from '@/lib/date-utils'
import {useToast} from '@/hooks/use-toast'
import {reportJavaScriptError} from '@/api/error-reporter'
import {routes} from '@/lib/routes'
import {useI18n} from '@/i18n'

/* eslint-disable i18next/no-literal-string -- const definitions are the source of truth */
export const PeriodOption = {
    LAST_24H: '24h',
    LAST_7D: '7d',
    LAST_30D: '30d',
    CUSTOM: 'custom',
    ALL: 'all',
} as const
/* eslint-enable i18next/no-literal-string */
export type PeriodOption = typeof PeriodOption[keyof typeof PeriodOption]

const STORAGE_KEY = 'background-jobs-time-range'

interface StoredTimeRange {
    period: PeriodOption
    from?: string
    to?: string
}

interface UseTimeRangeReturn {
    period: PeriodOption
    customFrom: string
    customTo: string
    handlePeriodChange: (newPeriod: PeriodOption) => void
    handleCustomRangeChange: (field: 'from' | 'to', value: string) => void
    toDatetimeLocal: (isoString: string | null) => string
    buildTimeQueryParams: () => string
}

/**
 * Hook to manage time range state with URL and localStorage persistence.
 * - URL params take precedence
 * - Falls back to localStorage if no URL params
 * - Updates localStorage when values change
 */
export function useTimeRange(defaultPeriod: PeriodOption = PeriodOption.LAST_24H): UseTimeRangeReturn {
    const [searchParams, setSearchParams] = useSearchParams()
    const {toast} = useToast()
    const i18n = useI18n()
    const reportedInvalidRef = useRef(false)

    const getStoredTimeRange = useCallback((): StoredTimeRange => {
        return safeLocalStorage.getJSON<StoredTimeRange>(STORAGE_KEY) ?? {period: defaultPeriod}
    }, [defaultPeriod])

    const saveTimeRange = useCallback((range: StoredTimeRange) => {
        safeLocalStorage.setJSON(STORAGE_KEY, range)
    }, [])

    // Get period from URL or localStorage
    const urlPeriod = searchParams.get('period') as PeriodOption | null
    const rawUrlFrom = searchParams.get('from')
    const rawUrlTo = searchParams.get('to')

    // WHY: URL params and localStorage are untrusted — garbage here must surface
    // as an explicit error toast (issue 861) instead of silently falling back to
    // an empty input. Bad params are stripped from the URL on the next render so
    // the rest of the hook sees a clean state and the input defaults work.
    const fromValid = rawUrlFrom == null || isValidInstantIso(rawUrlFrom)
    const toValid = rawUrlTo == null || isValidInstantIso(rawUrlTo)
    const urlFrom = fromValid ? rawUrlFrom : null
    const urlTo = toValid ? rawUrlTo : null

    useEffect(() => {
        if (fromValid && toValid) return
        if (reportedInvalidRef.current) return
        reportedInvalidRef.current = true

        const invalid: {param: 'from' | 'to', value: string}[] = []
        if (!fromValid && rawUrlFrom != null) invalid.push({param: 'from', value: rawUrlFrom})
        if (!toValid && rawUrlTo != null) invalid.push({param: 'to', value: rawUrlTo})

        const detail = invalid.map(i => `${i.param}=${i.value}`).join(', ')
        const message = i18n.timeRange.invalidUrlParam({detail})

        // eslint-disable-next-line i18next/no-literal-string -- error-reporter source identifier, internal
        void reportJavaScriptError(message, 'use-time-range').then(logId => {
            toast({
                variant: 'destructive',
                title: i18n.common.errorTitle,
                description: message,
                duration: 10_000,
                errorLogUrl: logId
                    ? routes.admin.errorLogs.detail(logId)
                    : routes.admin.errorLogs.list(),
            })
        })

        const params = new URLSearchParams(searchParams)
        invalid.forEach(i => params.delete(i.param))
        setSearchParams(params, {replace: true})
    }, [fromValid, toValid, rawUrlFrom, rawUrlTo, searchParams, setSearchParams, toast, i18n])

    // Determine effective values
    const stored = getStoredTimeRange()
    const period: PeriodOption = urlPeriod || stored.period || defaultPeriod
    const customFrom = urlFrom || (period === 'custom' ? stored.from : '') || ''
    const customTo = urlTo || (period === 'custom' ? stored.to : '') || ''

    // Sync URL to localStorage when URL has time range params
    useEffect(() => {
        if (urlPeriod) {
            const range: StoredTimeRange = {period: urlPeriod}
            if (urlPeriod === 'custom' && urlFrom && urlTo) {
                range.from = urlFrom
                range.to = urlTo
            }
            saveTimeRange(range)
        }
    }, [urlPeriod, urlFrom, urlTo, saveTimeRange])

    // On mount, if URL has no time range but localStorage does, update URL
    useEffect(() => {
        if (!urlPeriod && stored.period && stored.period !== defaultPeriod) {
            const params = new URLSearchParams(searchParams)
            params.set('period', stored.period)
            if (stored.period === 'custom' && stored.from && stored.to) {
                params.set('from', stored.from)
                params.set('to', stored.to)
            }
            setSearchParams(params, {replace: true})
        }
        // Only run on mount
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const handlePeriodChange = useCallback((newPeriod: PeriodOption) => {
        const params = new URLSearchParams(searchParams)

        // Remove old time params
        params.delete('period')
        params.delete('from')
        params.delete('to')

        const range: StoredTimeRange = {period: newPeriod}

        if (newPeriod === 'custom') {
            params.set('period', 'custom')
            const toIso = AppInstant.now()
            const fromIso = AppInstant.daysFromNow(-1)
            params.set('from', fromIso)
            params.set('to', toIso)
            range.from = fromIso
            range.to = toIso
        } else if (newPeriod !== defaultPeriod) {
            params.set('period', newPeriod)
        }

        saveTimeRange(range)
        setSearchParams(params)
    }, [searchParams, setSearchParams, saveTimeRange, defaultPeriod])

    const handleCustomRangeChange = useCallback((field: 'from' | 'to', value: string) => {
        const params = new URLSearchParams(searchParams)
        params.set('period', 'custom')

        let fromValue = field === 'from' ? '' : (params.get('from') || customFrom)
        let toValue = field === 'to' ? '' : (params.get('to') || customTo)

        if (value) {
            const isoValue = localDateTimeToIso(value)
            if (field === 'from') {
                fromValue = isoValue
            } else {
                toValue = isoValue
            }
            params.set(field, isoValue)
        } else {
            params.delete(field)
        }

        saveTimeRange({
            period: 'custom',
            from: fromValue,
            to: toValue
        })
        setSearchParams(params)
    }, [searchParams, setSearchParams, saveTimeRange, customFrom, customTo])

    const toDatetimeLocal = useCallback((isoString: string | null): string => {
        return localDateTimeForInput(isoString)
    }, [])

    const buildTimeQueryParams = useCallback((): string => {
        if (period === 'custom' && customFrom && customTo) {
            // eslint-disable-next-line i18next/no-literal-string
            return `&from=${customFrom}&to=${customTo}`
        }
        if (period === 'all' || period === defaultPeriod) {
            return ''
        }
        // eslint-disable-next-line i18next/no-literal-string
        return `&period=${period}`
    }, [period, customFrom, customTo, defaultPeriod])

    return {
        period,
        customFrom,
        customTo,
        handlePeriodChange,
        handleCustomRangeChange,
        toDatetimeLocal,
        buildTimeQueryParams
    }
}
